import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  BarChart3, TrendingUp, TrendingDown, Activity, RefreshCw,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple bar chart renderer using CSS
function MiniBarChart({ data, dataKey, color = "var(--qn-primary)", height = 120 }) {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d[dataKey] || 0), 1);
  
  return (
    <div className="flex items-end gap-[2px] w-full" style={{ height }}>
      {data.map((d, i) => {
        const val = d[dataKey] || 0;
        const barH = (val / maxVal) * height;
        return (
          <div
            key={i}
            className="flex-1 rounded-t-sm transition-all hover:opacity-80 group relative"
            style={{ height: barH, backgroundColor: color, minWidth: 4 }}
          >
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[var(--qn-card)] border border-[var(--qn-border)] rounded px-2 py-0.5 text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
              {d.date?.slice(5)}: {typeof val === 'number' && val < 1 ? `$${val.toFixed(4)}` : val.toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Sparkline
function Sparkline({ data, dataKey, color = "var(--qn-primary)", height = 40, width = 200 }) {
  if (!data || data.length < 2) return null;
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  }).join(" ");
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function UsageCharts() {
  const [timeseries, setTimeseries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when period changes; fetchData reads current period
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/gateway/analytics/timeseries?days=${period}`);
      setTimeseries(res.data.timeseries || []);
    } catch (e) {
      console.error("Failed to load timeseries:", e);
    }
    setLoading(false);
  };

  const totalRequests = timeseries.reduce((s, d) => s + d.requests, 0);
  const totalSavings = timeseries.reduce((s, d) => s + d.savings, 0);
  const totalTokens = timeseries.reduce((s, d) => s + d.tokens, 0);
  const avgLatency = timeseries.length > 0
    ? timeseries.reduce((s, d) => s + d.avg_latency_ms, 0) / timeseries.length
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-5 h-5 animate-spin text-[var(--qn-primary)]" />
      </div>
    );
  }

  if (timeseries.length === 0) {
    return (
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]">
        <CardContent className="py-12 text-center text-[var(--qn-foreground-muted)]">
          <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No gateway usage data yet. Make some API calls to see charts.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Activity className="w-4 h-4 text-[var(--qn-primary)]" />
          Gateway Usage Over Time
        </h3>
        <div className="flex gap-1">
          {[7, 14, 30].map(d => (
            <Button
              key={d}
              variant={period === d ? "default" : "outline"}
              size="sm"
              className={`h-7 text-xs ${period === d ? 'btn-primary' : ''}`}
              onClick={() => setPeriod(d)}
              data-testid={`period-${d}d`}
            >
              {d}d
            </Button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-[var(--qn-background)] rounded-lg p-3">
          <div className="text-xs text-[var(--qn-foreground-subtle)]">Requests ({period}d)</div>
          <div className="text-lg font-semibold">{totalRequests.toLocaleString()}</div>
          <Sparkline data={timeseries} dataKey="requests" color="var(--qn-primary)" height={24} width={100} />
        </div>
        <div className="bg-[var(--qn-background)] rounded-lg p-3">
          <div className="text-xs text-[var(--qn-foreground-subtle)]">Savings ({period}d)</div>
          <div className="text-lg font-semibold text-[var(--qn-success)]">${totalSavings.toFixed(4)}</div>
          <Sparkline data={timeseries} dataKey="savings" color="var(--qn-success)" height={24} width={100} />
        </div>
        <div className="bg-[var(--qn-background)] rounded-lg p-3">
          <div className="text-xs text-[var(--qn-foreground-subtle)]">Tokens ({period}d)</div>
          <div className="text-lg font-semibold">{totalTokens.toLocaleString()}</div>
          <Sparkline data={timeseries} dataKey="tokens" color="#06b6d4" height={24} width={100} />
        </div>
        <div className="bg-[var(--qn-background)] rounded-lg p-3">
          <div className="text-xs text-[var(--qn-foreground-subtle)]">Avg Latency</div>
          <div className="text-lg font-semibold">{avgLatency.toFixed(0)}ms</div>
          <Sparkline data={timeseries} dataKey="avg_latency_ms" color="#f59e0b" height={24} width={100} />
        </div>
      </div>

      {/* Request Volume Chart */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="requests-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Daily Requests</CardTitle>
        </CardHeader>
        <CardContent>
          <MiniBarChart data={timeseries} dataKey="requests" color="var(--qn-primary)" height={100} />
          <div className="flex justify-between text-xs text-[var(--qn-foreground-subtle)] mt-2">
            <span>{timeseries[0]?.date?.slice(5)}</span>
            <span>{timeseries[timeseries.length - 1]?.date?.slice(5)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Cost Comparison Chart */}
      <Card className="bg-[var(--qn-card)] border-[var(--qn-border)]" data-testid="savings-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            Daily Savings
            <Badge className="bg-[var(--qn-success)]/10 text-[var(--qn-success)] text-xs">
              <TrendingDown className="w-3 h-3 mr-1" />
              ${totalSavings.toFixed(4)} saved
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <MiniBarChart data={timeseries} dataKey="savings" color="var(--qn-success)" height={80} />
          <div className="flex justify-between text-xs text-[var(--qn-foreground-subtle)] mt-2">
            <span>{timeseries[0]?.date?.slice(5)}</span>
            <span>{timeseries[timeseries.length - 1]?.date?.slice(5)}</span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
